<template>
    <vue-recaptcha v-if="enabled"
                   class="recaptcha"
                   :sitekey="this.config.recaptcha.checkboxKey"
                   :style="(display ? '' : `display:none;`)"
                   @render="rendered"
                   @verify="verifyAsync" />
    <div v-else
         class="recaptcha recaptcha--disabled">
        reCAPTCHA disabled for local debug mode
    </div>
</template>

<script>
import ServiceFactory from '@/services/service-factory'

import VueRecaptcha from 'vue-recaptcha'

export default {

    props: {
        placeholder: {
            type: String,
            default: 'Recaptcha loading…',
        },
    },

    /**
     * @returns {{display: boolean, value: boolean, alreadyVerified: boolean}}
     */
    data() {
        return {
            alreadyVerified: false,
            value: false,
            display: false,
        }
    },

    computed: {

        /**
         * @returns {{routerBaseUrl: string, analyticsDebug: boolean, test: {showError: boolean}, localStorage: {disabled: boolean}, serverMaxBudget: number, apiPolling: {interval: number, attempts: number}, schemes, cssBreakpoints: {$small: number, $wide: number, $mid: number, $superwide: number, $wider: number}, analyticsIds: {default: string, dev: string}, baseUrl: string, default: {lowerBudgetChoice: number, midBudgetChoice: number, externalFontCss: string, registerInterestAt: string, showEvaluationConsent: boolean}, forceDevSchemeData: {id: number, enabled: boolean}, recaptcha: {checkboxKey: string, disableOnLocal: boolean, invisibleKey: string}, verboseRuleLogging: boolean, sharedNavigationItems: []}|*}
         */
        config() {
            return this.$config
        },

        /**
         * @returns {boolean}
         */
        enabled() {
            return !this.$config.recaptcha.disableOnLocal
        },
    },

    async beforeMount() {
        if (!this.enabled)
            await this._isVerifiedAlreadyAsync('bypassAsync')
    },

    methods: {

        async rendered() {
            await this._isVerifiedAlreadyAsync('isVerifiedAsync')
            this.display = true
        },

        /**
         * Checks verification status.
         * @param {string} verifyCheckFunc A function name.
         * @returns {Promise<Boolean>}
         * @private
         */
        async _isVerifiedAlreadyAsync(verifyCheckFunc) {
            try {
                const service = ServiceFactory.captcha()
                const operationState = await service[verifyCheckFunc]()
                this.alreadyVerified = operationState.error ? false : operationState.data.result
            } catch (err) {
                this.alreadyVerified = false
            } finally {
                this.value = this.alreadyVerified
                this.$emit('already-verified', this.alreadyVerified)
                this.$emit('verified', this.value)
            }

            return this.value
        },

        /**
         * Verify the ReCAPTCHA.
         * @async
         * @param {string} response
         * @returns {Promise<Boolean>}
         */
        async verifyAsync(response) {
            try {
                const service = ServiceFactory.captcha()
                const operationState = await service.verifyAsync(response)

                if (operationState.error) {
                    // noinspection ExceptionCaughtLocallyJS
                    throw new Error(operationState.error)
                }

                this.value = operationState.data.result
            } catch (err) {
                this.value = false
                this.$logger.error(`Unable to verify ReCaptcha input: ${ err.message }`)
            } finally {
                this.$emit('verified', this.value)
            }

            return this.value
        },
    },

    components: {
        VueRecaptcha,
    },
}
</script>

<style lang="scss">
// Ensure colours and other visual style elements are added to theme files, not placed in here.

.recaptcha {

    @include modifier("disabled") {
        font-weight: bold;
        color: darkred;
        text-align: left;
        padding: 1rem;
        border: lightgray solid 1px;
    }
}
</style>
